import type { FC, ReactNode } from 'react'
import type { ExperimentSummary } from '@/types'

import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/format'
import ExperimentStatus from '@/components/lab/experiment-status'
import TechnologyList from '@/components/projects/technology-list'
import TextLink from '@/components/elements/text-link'

interface ExperimentMetadataProps {
    experiment: ExperimentSummary
    className?: string
}

const Row: FC<{ term: string, children: ReactNode }> = ({ term, children }): ReactNode => (
    <div className='flex flex-col gap-1.5 border-t border-border py-3.5 first:border-t-0'>
        <dt className='font-mono text-label uppercase text-muted-foreground'>{term}</dt>
        <dd className='min-w-0 text-[0.9375rem]'>{children}</dd>
    </div>
)

/** Spec sheet for a lab entry, styled like the header block of a technical document. */
const ExperimentMetadata: FC<ExperimentMetadataProps> = ({ experiment, className }): ReactNode => {
    return (
        <dl className={cn('border-y border-foreground', className)}>
            <Row term='Entry'>{experiment.code}</Row>

            <Row term='Status'>
                <ExperimentStatus status={experiment.status} />
            </Row>

            <Row term='Category'>{experiment.categoryLabel}</Row>

            <Row term='Started'>
                <time dateTime={experiment.date}>{formatDate(experiment.date)}</time>
            </Row>

            {experiment.updatedAt && (
                <Row term='Last updated'>
                    <time dateTime={experiment.updatedAt}>{formatDate(experiment.updatedAt)}</time>
                </Row>
            )}

            <Row term='Technologies'>
                <TechnologyList items={experiment.technologies} variant='tags' className='pt-0.5' />
            </Row>

            {experiment.sourceUrl && (
                <Row term='Source'>
                    <TextLink href={experiment.sourceUrl} arrow>View source</TextLink>
                </Row>
            )}
        </dl>
    )
}

export default ExperimentMetadata
